import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil, map, filter, debounceTime, switchMap, take, distinctUntilChanged } from 'rxjs/operators';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { UIStateService, PanelPosition } from '../services/ui-state.service';
import { ResponsiveLayoutService, LayoutConfig } from '../services/responsive-layout.service';
import { PositionCalculatorService } from './position-calculator.service';
import { PropertyGrouperService, PropertyGroup } from './property-grouper.service';
import { DragDropPropertyManagerService, DraggablePropertyGroup } from './drag-drop-property-manager.service';
import { PropertyPresetsService, PropertyPreset } from './property-presets.service';
import { AIPropertySuggestionsService, PropertySuggestion } from '../ai-suggestions/ai-property-suggestions.service';
import { SmartPanelManagerService } from '../services/smart-panel-manager.service';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { BuilderNode } from '../interfaces/builder-screen.models';

@Component({
  selector: 'app-floating-inspector',
  templateUrl: './floating-inspector.component.html',
  styleUrls: ['./floating-inspector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:false
})
export class FloatingInspectorComponent implements OnInit, OnDestroy {
  @ViewChild('inspectorPanel', { static: false }) inspectorPanel!: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  
  // CORREÇÃO CRÍTICA: Bloqueios de operação para prevenir condições de corrida e travamento de tela
  private isResettingState = false;
  private isProcessingComponentChange = false;
  private isClosingPanels = false;
  private lastProcessedComponentId: string | null = null;
  private resizeTimeout: any;
  private scrollTimeout: any;
  
  // CORREÇÃO CRÍTICA: Bloqueios de operação do seletor de cores
  private isUpdatingColorProperty = false;
  private colorUpdateQueue: Array<{ property: string; value: string }> = [];
  
  visible$ = this.uiState.inspectorVisible$;
  selectedComponent$ = this.uiState.selectedComponent$;
  searchTerm$ = this.uiState.searchTerm$;
  layoutConfig$ = this.responsiveLayout.layoutConfig$;
  
  currentComponent$: Observable<BuilderNode | null> = combineLatest([
    this.selectedComponent$,
    this.builderService.nodes$
  ]).pipe(
    map(([selectedId, nodes]) => 
      selectedId ? nodes.find(node => node.id === selectedId) || null : null
    )
  );

  propertyGroups$: Observable<PropertyGroup[]> = this.currentComponent$.pipe(
    filter(component => component !== null),
    map(component => this.propertyGrouper.groupProperties(component!))
  );

  filteredGroups$: Observable<PropertyGroup[]> = combineLatest([
    this.propertyGroups$,
    this.searchTerm$,
    this.smartPanelManager.currentPreset$
  ]).pipe(
    map(([groups, searchTerm, activePreset]) => {
      console.log(`🔄 FILTERED GROUPS OBSERVABLE TRIGGERED`);
      console.log(`  - Groups count: ${groups.length}`);
      console.log(`  - Search term: "${searchTerm}"`);
      console.log(`  - Active preset: "${activePreset}"`);
      
      // Primeiro filtra por preset (quais painéis devem estar visíveis)
      const presetFilteredGroups = this.filterGroupsByPreset(groups, activePreset || 'standard');
      
      // Depois filtra por termo de busca
      const searchFiltered = this.filterGroups(presetFilteredGroups, searchTerm);
      
      this.currentFilteredGroups = searchFiltered; // Armazena para contagem de resultados de busca
      
      console.log(`🎯 FINAL FILTERED GROUPS: ${searchFiltered.length} groups`);
      console.log(`📋 Final group IDs:`, searchFiltered.map(g => g.id));
      
      return searchFiltered;
    })
  );


  availablePresets$ = this.currentComponent$.pipe(
    switchMap(component => 
      component ? this.presetsService.getPresetsForComponent(component.type) : []
    )
  );


  aiSuggestions$ = this.currentComponent$.pipe(
    switchMap(component => {
      if (!component) return [];
      
      const context = this.buildComponentContext(component);
      return this.aiSuggestions.getSuggestions(component, context);
    })
  );

  inspectorPosition: PanelPosition | null = null;
  isDragging = false;
  isResizing = false;
  dragOffset = { x: 0, y: 0 };
  resizeDirection: 'right' | 'bottom' | 'corner' | null = null;
  resizeStartPosition = { x: 0, y: 0 };
  resizeStartSize = { width: 0, height: 0 };
  

  showPresets = false;
  showAISuggestions = false;
  collapsedGroups = new Set<string>();
  
  // Guia do usuário para usuários não técnicos
  showUserGuide = true; // Mostra por padrão para usuários iniciantes
  
  // Chave de re-renderização forçada para mudanças de preset
  inspectorKey = 0;
  
  // Previne atualizações recursivas de posição
  private isUpdatingPosition = false;

  constructor(
    private uiState: UIStateService,
    private responsiveLayout: ResponsiveLayoutService,
    private positionCalculator: PositionCalculatorService,
    public propertyGrouper: PropertyGrouperService,
    private dragDropManager: DragDropPropertyManagerService,
    private presetsService: PropertyPresetsService,
    private aiSuggestions: AIPropertySuggestionsService,
    private smartPanelManager: SmartPanelManagerService,
    private builderService: BuilderScreenBetaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Verifica se o usuário ocultou o guia antes
    const hideGuide = localStorage.getItem('builder-screen-hide-guide');
    if (hideGuide === 'true') {
      this.showUserGuide = false;
    }

    // CORREÇÃO CRÍTICA: Reset do estado do inspetor quando o componente muda com debouncing e prevenção de condições de corrida
    this.currentComponent$.pipe(
      takeUntil(this.destroy$),
      filter(component => component !== null),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id), // Emite apenas quando o componente realmente muda
      debounceTime(100) // Debounce de mudanças rápidas de componente para prevenir travamento
    ).subscribe(component => {
      if (component && !this.isProcessingComponentChange) {
        // Previne processamento concorrente
        if (this.lastProcessedComponentId !== component.id) {
          console.log(`🔄 COMPONENT CHANGED (debounced): ${this.lastProcessedComponentId} → ${component.id} (${component.type})`);
          
          // Define bloqueio de processamento
          this.isProcessingComponentChange = true;
          
          // Reseta estado do inspetor para novo componente com tratamento de erro
          this.safeResetInspectorState(component)
            .then(() => {
              this.updateInspectorPosition(component.id);
              this.lastProcessedComponentId = component.id;
            })
            .catch(error => {
              console.error(`❌ Error resetting inspector state:`, error);
            })
            .finally(() => {
              // Sempre libera o bloqueio
              this.isProcessingComponentChange = false;
            });
        } else {
          console.log(`📋 Same component selected: ${component.id} (${component.type}) - no reset needed`);
        }
      }
    });

    // Atualiza posição no redimensionamento da janela com debouncing
    window.addEventListener('resize', this.onWindowResize.bind(this));
    window.addEventListener('scroll', this.onWindowScroll.bind(this));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Limpa timeouts pendentes
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    window.removeEventListener('scroll', this.onWindowScroll.bind(this));
  }

  // CORREÇÃO CRÍTICA: Manipuladores de eventos de janela com debounce para prevenir travamento de tela

  private onWindowResize(event: Event) {
    // Debouncing aprimorado para prevenir travamento de tela
    if (this.isUpdatingPosition || this.isResettingState || this.isProcessingComponentChange) {
      return;
    }
    
    // Limpa timeout anterior
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Debounce de eventos de redimensionamento com delay maior
    this.resizeTimeout = setTimeout(() => {
      if (!this.isUpdatingPosition && !this.isResettingState && !this.isProcessingComponentChange) {
        const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
        if (selectedComponent) {
          this.updateInspectorPosition(selectedComponent);
        }
      }
    }, 250); // Tempo de debounce aumentado
  }

  private onWindowScroll(event: Event) {
    // Debouncing aprimorado para prevenir travamento de tela
    if (this.isUpdatingPosition || this.isResettingState || this.isProcessingComponentChange) {
      return;
    }
    
    // Limpa timeout anterior
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    // Debounce de eventos de rolagem com delay maior
    this.scrollTimeout = setTimeout(() => {
      if (!this.isUpdatingPosition && !this.isResettingState && !this.isProcessingComponentChange) {
        const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
        if (selectedComponent) {
          this.updateInspectorPosition(selectedComponent);
        }
      }
    }, 150); // Tempo de debounce aumentado
  }

  private updateInspectorPosition(componentId: string) {
    // CORREÇÃO CRÍTICA: Previne chamadas recursivas
    if (this.isUpdatingPosition) {
      return;
    }
    
    this.isUpdatingPosition = true;
    
    try {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // POSICIONAMENTO APRIMORADO: Sempre usa posição fixa e não intrusiva
      // Posiciona inspetor no lado direito, mas garante que não cubra componentes
      const inspectorWidth = Math.min(420, viewportWidth * 0.3); // Largura responsiva
      const inspectorHeight = Math.min(700, viewportHeight * 0.85); // Mais alto para melhor visibilidade do conteúdo
      
      // Posição fixa no lado direito com margens seguras
      const rightMargin = 20;
      const topMargin = 80; // Deixa espaço para cabeçalho/navegação
      
      this.inspectorPosition = {
        x: viewportWidth - inspectorWidth - rightMargin,
        y: topMargin,
        width: inspectorWidth,
        height: inspectorHeight,
      };
      
      // Garante que a posição esteja dentro dos limites da viewport
      if (this.inspectorPosition.x < 20) {
        this.inspectorPosition.x = 20;
      }
      
      if (this.inspectorPosition.y + this.inspectorPosition.height > viewportHeight - 20) {
        this.inspectorPosition.y = viewportHeight - this.inspectorPosition.height - 20;
      }
      
      // CORREÇÃO CRÍTICA: Só atualiza estado da UI se a posição realmente mudou
      const currentState = this.uiState.getCurrentState();
      const currentPosition = currentState.inspector.position;
      
      if (!currentPosition || 
          currentPosition.x !== this.inspectorPosition.x || 
          currentPosition.y !== this.inspectorPosition.y ||
          currentPosition.width !== this.inspectorPosition.width ||
          currentPosition.height !== this.inspectorPosition.height) {
        
        // Usa setTimeout para quebrar a pilha de chamadas
        setTimeout(() => {
          this.uiState.updateInspector({ position: this.inspectorPosition });
        }, 0);
      }
      
      this.cdr.detectChanges();
    } finally {
      // Sempre reseta a flag
      setTimeout(() => {
        this.isUpdatingPosition = false;
      }, 100);
    }
  }

  private isPositionValid(position: PanelPosition): boolean {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Verifica se a posição está dentro dos limites da viewport
    return (
      position.x >= 0 &&
      position.y >= 0 &&
      position.x + position.width <= viewportWidth &&
      position.y + position.height <= viewportHeight
    );
  }



  private filterGroupsByPreset(groups: PropertyGroup[], activePreset: string): PropertyGroup[] {
    // Define mapeamento de visibilidade de painéis do preset (deve corresponder ao panel-preset-selector)
    const presetPanelConfig: { [key: string]: string[] } = {
      'minimal': ['essential'],
      'standard': ['essential', 'appearance', 'layout'],
      'advanced': ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced']
    };

    const visiblePanels = presetPanelConfig[activePreset] || ['essential'];
    
    console.log(`🔍 FILTERING GROUPS BY PRESET "${activePreset}"`);
    console.log(`📋 Available groups:`, groups.map(g => g.id));
    console.log(`👁️ Visible panels for preset:`, visiblePanels);
    
    // Filtra grupos para incluir apenas aqueles que devem estar visíveis para este preset
    const filteredGroups = groups.filter(group => {
      const shouldBeVisible = visiblePanels.includes(group.id);
      if (!shouldBeVisible) {
        console.log(`❌ HIDING group "${group.id}" (not in preset "${activePreset}")`);
      } else {
        console.log(`✅ SHOWING group "${group.id}" (included in preset "${activePreset}")`);
      }
      return shouldBeVisible;
    });

    console.log(`📊 PRESET FILTERING RESULT: ${filteredGroups.length}/${groups.length} groups visible`);
    console.log(`🎯 Final visible groups:`, filteredGroups.map(g => g.id));
    
    return filteredGroups;
  }

  private filterGroups(groups: PropertyGroup[], searchTerm: string): PropertyGroup[] {
    if (!searchTerm.trim()) {
      return groups;
    }

    const term = searchTerm.toLowerCase();
    return groups.map(group => ({
      ...group,
      properties: group.properties.filter(prop => {
        const displayName = this.propertyGrouper.getPropertyDisplayName(prop).toLowerCase();
        return displayName.includes(term) || prop.toLowerCase().includes(term);
      })
    })).filter(group => group.properties.length > 0);
  }

  onSearchChange(term: string) {
    this.uiState.setSearchTerm(term);
  }

  clearSearch() {
    this.uiState.setSearchTerm('');
  }

  // Armazena grupos filtrados para contagem de resultados de busca
  private currentFilteredGroups: PropertyGroup[] = [];

  getSearchResultsCount(): number {
    return this.currentFilteredGroups.reduce((count, group) => count + group.properties.length, 0);
  }



  onCloseInspector(event?: Event) {
    console.log('Close inspector clicked'); // Log de debug
    
    // Previne propagação do evento se o evento for fornecido
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    this.uiState.hideInspector();
    this.cdr.markForCheck(); // Força detecção de mudança
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    // Só fecha se o inspetor estiver visível e nenhum input estiver focado
    const activeElement = document.activeElement as HTMLElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    
    if (!isInputFocused) {
      this.onCloseInspector();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onStartDrag(event: MouseEvent) {
    if (!this.inspectorPosition) return;

    // Não inicia arraste se clicar em botões ou elementos interativos
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.header-actions')) {
      return;
    }

    this.isDragging = true;
    this.dragOffset = {
      x: event.clientX - this.inspectorPosition.x,
      y: event.clientY - this.inspectorPosition.y,
    };

    document.addEventListener('mousemove', this.onDrag.bind(this));
    document.addEventListener('mouseup', this.onEndDrag.bind(this));
    event.preventDefault();
  }

  private onDrag(event: MouseEvent) {
    if (!this.isDragging || !this.inspectorPosition) return;

    const newX = event.clientX - this.dragOffset.x;
    const newY = event.clientY - this.dragOffset.y;
    
    // Restringe aos limites da viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const inspectorWidth = this.inspectorPosition.width;
    const inspectorHeight = this.inspectorPosition.height;
    
    const constrainedX = Math.max(0, Math.min(newX, viewportWidth - inspectorWidth));
    const constrainedY = Math.max(0, Math.min(newY, viewportHeight - inspectorHeight));

    this.inspectorPosition = {
      ...this.inspectorPosition,
      x: constrainedX,
      y: constrainedY,
    };

    this.cdr.detectChanges();
  }

  private onEndDrag() {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onDrag.bind(this));
    document.removeEventListener('mouseup', this.onEndDrag.bind(this));

    // CORREÇÃO CRÍTICA: Previne chamadas recursivas ao atualizar posição após arraste
    if (this.inspectorPosition && !this.isUpdatingPosition) {
      setTimeout(() => {
        this.uiState.updateInspector({ position: this.inspectorPosition });
      }, 0);
    }
  }

  onStartResize(direction: 'right' | 'bottom' | 'corner', event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.inspectorPosition) return;

    this.isResizing = true;
    this.resizeDirection = direction;
    this.resizeStartPosition = { x: event.clientX, y: event.clientY };
    this.resizeStartSize = { 
      width: this.inspectorPosition.width || 320, 
      height: this.inspectorPosition.height || 400 
    };

    document.addEventListener('mousemove', this.onResize.bind(this));
    document.addEventListener('mouseup', this.onEndResize.bind(this));
    
    this.cdr.detectChanges();
  }

  private onResize(event: MouseEvent) {
    if (!this.isResizing || !this.inspectorPosition || !this.resizeDirection) return;

    const deltaX = event.clientX - this.resizeStartPosition.x;
    const deltaY = event.clientY - this.resizeStartPosition.y;

    let newWidth = this.resizeStartSize.width;
    let newHeight = this.resizeStartSize.height;

    switch (this.resizeDirection) {
      case 'right':
        newWidth = Math.max(320, this.resizeStartSize.width + deltaX);
        break;
      case 'bottom':
        newHeight = Math.max(200, this.resizeStartSize.height + deltaY);
        break;
      case 'corner':
        newWidth = Math.max(320, this.resizeStartSize.width + deltaX);
        newHeight = Math.max(200, this.resizeStartSize.height + deltaY);
        break;
    }

    // Aplica restrições máximas
    newWidth = Math.min(newWidth, window.innerWidth * 0.8);
    newHeight = Math.min(newHeight, window.innerHeight * 0.8);

    this.inspectorPosition = {
      ...this.inspectorPosition,
      width: newWidth,
      height: newHeight
    };

    this.cdr.detectChanges();
  }

  private onEndResize() {
    this.isResizing = false;
    this.resizeDirection = null;
    document.removeEventListener('mousemove', this.onResize.bind(this));
    document.removeEventListener('mouseup', this.onEndResize.bind(this));

    // CORREÇÃO CRÍTICA: Previne chamadas recursivas ao atualizar posição após redimensionamento
    if (this.inspectorPosition && !this.isUpdatingPosition) {
      setTimeout(() => {
        this.uiState.updateInspector({ position: this.inspectorPosition });
      }, 0);
    }
  }



  trackByGroupId(index: number, group: PropertyGroup): string {
    // Inclui chave do inspetor para forçar re-renderização quando presets mudam
    return `${group.id}-${this.inspectorKey}`;
  }

  trackByProperty(index: number, property: string): string {
    return property;
  }

  onResetProperties() {
    const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
    if (selectedComponent) {
      // Reseta propriedades do componente para padrões
      this.builderService.resetNodeProperties(selectedComponent);
    }
  }

  onDuplicateComponent() {
    const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
    if (selectedComponent) {
      // Duplica o componente selecionado
      this.builderService.duplicateNode(selectedComponent);
    }
  }

  // Fase 3: Métodos de Arrastar e Soltar
  onPropertyGroupDrop(event: CdkDragDrop<DraggablePropertyGroup[]>) {
    this.currentComponent$.pipe(takeUntil(this.destroy$)).subscribe(component => {
      if (component) {
        const reorderedGroups = this.dragDropManager.onPropertyGroupDrop(event, component.type);
        this.cdr.detectChanges();
      }
    });
  }

  onPropertyDrop(event: CdkDragDrop<string[]>, groupId: string) {
    this.currentComponent$.pipe(takeUntil(this.destroy$)).subscribe(component => {
      if (component) {
        const reorderedProperties = this.dragDropManager.onPropertyDrop(event, groupId, component.type);
        this.cdr.detectChanges();
      }
    });
  }

  // Fase 3: Métodos de Presets de Propriedades
  togglePresets() {
    this.showPresets = !this.showPresets;
    this.cdr.detectChanges();
  }

  applyPreset(preset: PropertyPreset) {
    this.currentComponent$.pipe(takeUntil(this.destroy$)).subscribe(component => {
      if (component) {
        const updatedComponent = {
          ...component,
          props: { ...component.props, ...preset.properties }
        };
        this.builderService.updateNode(updatedComponent);
        this.presetsService.applyPreset(preset.id);
      }
    });
  }

  createPresetFromCurrent() {
    this.currentComponent$.pipe(takeUntil(this.destroy$)).subscribe(component => {
      if (component) {
        const name = prompt('Enter preset name:');
        if (name) {
          const description = prompt('Enter preset description:') || '';
          this.presetsService.createCustomPreset(
            name,
            description,
            component.type,
            component.props as Record<string, any>
          );
        }
      }
    });
  }

  // Fase 3: Métodos de Sugestões de IA
  toggleAISuggestions() {
    this.showAISuggestions = !this.showAISuggestions;
    this.cdr.detectChanges();
  }

  applySuggestion(suggestion: PropertySuggestion) {
    this.currentComponent$.pipe(takeUntil(this.destroy$)).subscribe(component => {
      if (component) {
        const updatedComponent = this.aiSuggestions.applySuggestion(suggestion, component);
        this.builderService.updateNode(updatedComponent);
      }
    });
  }

  dismissSuggestion(suggestionId: string, reason: 'not_relevant' | 'already_done' | 'not_interested') {
    this.aiSuggestions.dismissSuggestion(suggestionId, reason);
  }

  // Fase 3: Métodos Auxiliares
  private buildComponentContext(component: BuilderNode): any {
    // Constrói contexto para sugestões de IA
    return {
      componentType: component.type,
      currentProperties: component.props,
      siblingComponents: [], // Obteria do builder service
      userBehavior: {
        recentActions: [],
        commonPatterns: [],
        preferredStyles: {},
        errorHistory: [],
      },
      formContext: {
        totalComponents: 5, // Calcularia do builder service
        hasValidation: false,
        theme: 'light',
        purpose: 'form',
      },
    };
  }

  onGroupToggle(groupId: string) {
    const currentState = this.uiState.getCurrentState();
    const currentComponent = currentState.inspector.selectedComponent;
    if (!currentComponent) return;

    const isCurrentlyExpanded = !this.isGroupCollapsed(groupId);
    const newExpandedState = !isCurrentlyExpanded;
    
    // CORREÇÃO CRÍTICA: Permite apenas um painel aberto por vez para melhor UX
    if (newExpandedState) {
      // Fecha todos os outros painéis primeiro
      this.collapsedGroups.clear();
      const allPanels = ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'];
      allPanels.forEach(panelId => {
        if (panelId !== groupId) {
          this.collapsedGroups.add(panelId);
          this.smartPanelManager.updatePanelState(currentComponent as any, panelId, false);
        }
      });
      
      // Então abre o painel selecionado
      this.collapsedGroups.delete(groupId);
      this.smartPanelManager.updatePanelState(currentComponent as any, groupId, true);
    } else {
      // Fecha o painel
      this.collapsedGroups.add(groupId);
      this.smartPanelManager.updatePanelState(currentComponent as any, groupId, false);
    }
    
    this.cdr.markForCheck();
  }

  closeGroup(groupId: string, event: Event) {
    // Previne propagação do evento para evitar disparar alternância do painel
    event.stopPropagation();
    event.preventDefault();
    
    // Colapsa o grupo
    this.collapsedGroups.add(groupId);
    this.cdr.markForCheck();
  }

  isGroupCollapsed(groupId: string): boolean {
    const currentState = this.uiState.getCurrentState();
    const currentComponent = currentState.inspector.selectedComponent;
    if (!currentComponent) return false;
    
    // Usa gerenciador inteligente de painéis para determinar estado
    const smartState = this.smartPanelManager.getPanelState(currentComponent as any, groupId);
    return !smartState; // Gerenciador inteligente retorna true para expandido, precisamos true para colapsado
  }

  onPresetSelected(presetId: string) {
    const currentState = this.uiState.getCurrentState();
    const currentComponent = currentState.inspector.selectedComponent;
    if (!currentComponent) {
      console.warn(`❌ Cannot apply preset "${presetId}" - no component selected`);
      return;
    }
    
    console.log(`🎯 Preset "${presetId}" selected for component: ${currentComponent}`);
    
    // CORREÇÃO CRÍTICA: Atualiza preset atual PRIMEIRO para disparar observable filteredGroups$
    this.smartPanelManager.setCurrentPreset(presetId);
    
    // Aplica preset através do gerenciador inteligente de painéis
    this.smartPanelManager.applyPreset(currentComponent as any, presetId);
    
    // FUNCIONALIDADE INSTANTÂNEA: Força atualização imediata e completa da UI
    this.applyPresetInstantly(presetId, currentComponent);
    
    // Mostra mensagem de feedback do usuário
    this.showPresetAppliedFeedback(presetId);
  }

  private applyPresetInstantly(presetId: string, componentId: string) {
    console.log(`🔥 Applying preset "${presetId}" instantly to floating inspector`);
    
    // 1. Força atualização imediata da UI com múltiplas estratégias
    this.forceInspectorRerender(presetId);
    
    // 2. Dispara múltiplos ciclos de detecção de mudança para garantir atualização dos observables
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // 3. Atualização adicional após atualizações do DOM para garantir atualizações do filteredGroups$
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
    
    // 4. Atualização final para garantir que tudo esteja sincronizado
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 100);
    
    console.log(`⚡ Preset "${presetId}" applied instantly to inspector with forced refresh`);
  }

  private applyPresetPanelVisibility(presetId: string, componentId: string) {
    // Define mapeamento de visibilidade de painéis do preset
    const presetPanelConfig: { [key: string]: string[] } = {
      'minimal': ['essential'],
      'standard': ['essential', 'appearance', 'layout'],
      'advanced': ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced']
    };

    const visiblePanels = presetPanelConfig[presetId] || ['essential'];
    const allPanels = ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'];

    console.log(`📋 Setting panel visibility for preset "${presetId}":`, visiblePanels);

    // Atualiza grupos colapsados baseado no preset
    this.collapsedGroups.clear();
    
    allPanels.forEach(panelId => {
      const shouldBeVisible = visiblePanels.includes(panelId);
      
      if (!shouldBeVisible) {
        this.collapsedGroups.add(panelId);
      }
      
      // Também atualiza o gerenciador inteligente de painéis
      this.smartPanelManager.updatePanelState(componentId as any, panelId, shouldBeVisible);
    });

    console.log(`✅ Panel visibility applied. Collapsed panels:`, Array.from(this.collapsedGroups));
  }

  /**
   * Safe force inspector rerender with timeout protection
   */
  private async safeForceInspectorRerender(presetId: string): Promise<void> {
    return new Promise((resolve) => {
      try {
        const currentState = this.uiState.getCurrentState();
        const currentComponent = currentState.inspector.selectedComponent;
        if (!currentComponent) {
          resolve();
          return;
        }

        console.log(`🔄 Safe force rerendering inspector for preset: ${presetId}`);

        // 1. Força re-renderização atualizando a chave (isso força trackBy a re-renderizar todos os grupos)
        this.inspectorKey = Date.now();
        
        // 2. Dispara detecção de mudança com proteção de timeout
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        
        // 3. Agenda ciclos adicionais de atualização com timing adequado
        let refreshCount = 0;
        const maxRefreshes = 3;
        
        const scheduleRefresh = () => {
          if (refreshCount < maxRefreshes) {
            refreshCount++;
            setTimeout(() => {
              try {
                this.cdr.markForCheck();
                if (refreshCount === 1) {
                  this.cdr.detectChanges();
                }
                scheduleRefresh();
              } catch (error) {
                console.error(`❌ Error during refresh cycle ${refreshCount}:`, error);
                resolve();
              }
            }, refreshCount * 25); // Timing escalonado: 25ms, 50ms, 75ms
          } else {
            resolve();
          }
        };
        
        scheduleRefresh();
        
      } catch (error) {
        console.error(`❌ Error during safe force rerender:`, error);
        resolve(); // Não bloqueia o processo
      }
    });
  }

  /**
   * Método legado para compatibilidade com versões anteriores
   */
  private forceInspectorRerender(presetId: string) {
    this.safeForceInspectorRerender(presetId).catch(error => {
      console.error(`❌ Error in legacy forceInspectorRerender:`, error);
    });
    
    // Mantém lógica original para fallback imediato
    const currentState = this.uiState.getCurrentState();
    const currentComponent = currentState.inspector.selectedComponent;
    if (!currentComponent) return;

    console.log(`🔄 Legacy force rerendering inspector for preset: ${presetId}`);

    // 1. Força re-renderização atualizando a chave (isso força trackBy a re-renderizar todos os grupos)
    this.inspectorKey = Date.now();
    
    // 2. Dispara múltiplas estratégias de detecção de mudança
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // 3. Ciclos adicionais de atualização para garantir atualização dos observables
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
    
    setTimeout(() => {
      this.cdr.markForCheck();
    }, 50);
    
    // 4. Registra o estado atual para debug
    setTimeout(() => {
      this.debugCurrentInspectorState(presetId);
    }, 100);
    
    console.log(`✅ Inspector force-rerendered for preset: ${presetId}`);
  }

  private debugCurrentInspectorState(presetId: string) {
    console.log(`🔍 Debugging inspector state after preset "${presetId}" application:`);
    
            // Verifica preset atual no gerenciador inteligente de painéis
        const currentPreset = this.smartPanelManager.getCurrentPreset();
        console.log(`📋 Smart panel manager current preset: ${currentPreset}`);
        
        // Verifica grupos visíveis no DOM
        const visibleGroups = document.querySelectorAll('.ant-collapse-item:not(.ant-collapse-item-disabled)');
    console.log(`👁️ Visible groups in DOM: ${visibleGroups.length}`);
    
    visibleGroups.forEach((group, index) => {
      const header = group.querySelector('.ant-collapse-header');
      const headerText = header?.textContent?.trim();
      console.log(`  ${index + 1}. ${headerText}`);
    });
  }

  private showPresetAppliedFeedback(presetId: string) {
    // Importa NzMessageService se não estiver já importado
    const presetNames = {
      'minimal': 'Mínimo',
      'standard': 'Padrão', 
      'advanced': 'Avançado'
    };
    
    const presetName = presetNames[presetId as keyof typeof presetNames] || presetId;
    
    // Você pode injetar NzMessageService e mostrar uma mensagem toast
    console.log(`✅ Predefinição "${presetName}" aplicada com sucesso!`);
    
    // Feedback visual: destaca brevemente o seletor de preset
    const presetElement = document.querySelector('.panel-preset-selector');
    if (presetElement) {
      presetElement.classList.add('preset-applied');
      setTimeout(() => {
        presetElement.classList.remove('preset-applied');
      }, 1000);
    }
  }

  private updateCollapsedGroupsFromSmartManager(componentType: string) {
    const allPanels = ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'];
    this.collapsedGroups.clear();
    
    allPanels.forEach(panelId => {
      const isExpanded = this.smartPanelManager.getPanelState(componentType as any, panelId);
      if (!isExpanded) {
        this.collapsedGroups.add(panelId);
      }
    });
  }

  hideUserGuide() {
    this.showUserGuide = false;
    // Salva preferência do usuário para não mostrar guia novamente
    localStorage.setItem('builder-screen-hide-guide', 'true');
  }

  /**
   * CORREÇÃO CRÍTICA: Reset seguro do estado do inspetor com tratamento adequado de erro e prevenção de condições de corrida
   */
  private async safeResetInspectorState(component: BuilderNode): Promise<void> {
    // Previne resets concorrentes
    if (this.isResettingState || this.isUpdatingPosition) {
      console.warn(`⚠️ Reset already in progress, skipping for component: ${component.id}`);
      return Promise.resolve();
    }
    
    try {
      // Define bloqueio de reset
      this.isResettingState = true;
      
      console.log(`🔄 SAFE RESETTING INSPECTOR STATE for new component: ${component.type} (${component.id})`);
      
      // 1. FECHA TODOS OS PAINÉIS ABERTOS ao trocar componentes (com proteção de timeout)
      await this.safeCloseAllOpenPanels();
      
      // 2. Reseta todo o estado da UI para o novo componente
      this.collapsedGroups.clear();
      
      // 3. Reseta termo de busca para mostrar todas as propriedades do novo componente
      this.uiState.updateInspector({ searchTerm: '' });
      
      // 4. Aplica estado padrão do painel baseado no tipo de componente
      const defaultPreset = this.getDefaultPresetForComponent(component.type);
      this.smartPanelManager.setCurrentPreset(defaultPreset);
      
      // Adiciona pequeno delay para prevenir condições de corrida
      await new Promise(resolve => setTimeout(resolve, 50));
      
      this.smartPanelManager.applyPreset(component.type, defaultPreset);
      
      // 5. Força re-renderização completa para troca de componente (com proteção de timeout)
      await this.safeForceInspectorRerender(defaultPreset);
      
      // 6. Dispara detecção de mudança para atualizar todos os componentes filhos
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      console.log(`✅ Safe inspector state reset complete for component: ${component.type} with preset: ${defaultPreset}`);
      
    } catch (error) {
      console.error(`❌ Error during safe inspector state reset:`, error);
      throw error;
    } finally {
      // Sempre libera o bloqueio de reset
      this.isResettingState = false;
    }
  }

  /**
   * Método legado para compatibilidade com versões anteriores - agora chama versão segura
   */
  private resetInspectorState(component: BuilderNode) {
    this.safeResetInspectorState(component).catch(error => {
      console.error(`❌ Error in legacy resetInspectorState:`, error);
    });
  }

  private getDefaultPresetForComponent(componentType: string): string {
    // Retorna preset padrão apropriado baseado na complexidade do componente
    const complexComponents = ['table', 'simpletable', 'tabs'];
    const basicComponents = ['text', 'textarea', 'number', 'switch', 'button'];
    
    if (basicComponents.includes(componentType)) {
      return 'minimal';
    } else if (complexComponents.includes(componentType)) {
      return 'advanced';
    } else {
      return 'standard';
    }
  }

  /**
   * CORREÇÃO CRÍTICA: Fecha com segurança todos os painéis abertos com proteção de timeout
   */
  private async safeCloseAllOpenPanels(): Promise<void> {
    // Previne operações concorrentes de fechamento de painéis
    if (this.isClosingPanels) {
      console.warn(`⚠️ Panel closing already in progress, skipping`);
      return Promise.resolve();
    }
    
    try {
      this.isClosingPanels = true;
      console.log(`🚪 SAFE CLOSING ALL OPEN PANELS for component switch`);
      
      // Fecha painéis com proteção de timeout
      const closeOperations = [
        this.safeClosePresetPanel(),
        // TEMPORARIAMENTE DESABILITADO: Edição Rápida
        // this.safeCloseQuickEditPanel(),
        this.safeCloseModalDialogs(),
        this.safeResetTemporaryStates()
      ];
      
      // Aguarda todas as operações de fechamento com timeout
      await Promise.race([
        Promise.all(closeOperations),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Panel closing timeout')), 2000))
      ]);
      
      console.log(`✅ All panels closed safely`);
      
    } catch (error) {
      console.error(`❌ Error closing panels:`, error);
      // Não lança erro - continua com o processo de reset
    } finally {
      this.isClosingPanels = false;
    }
  }

  /**
   * Método legado para compatibilidade com versões anteriores
   */
  private closeAllOpenPanels() {
    this.safeCloseAllOpenPanels().catch(error => {
      console.error(`❌ Error in legacy closeAllOpenPanels:`, error);
    });
  }
  
  /**
   * Fecha com segurança o painel de preset com proteção de timeout
   */
  private async safeClosePresetPanel(): Promise<void> {
    return new Promise((resolve) => {
      try {
        console.log(`🎯 Safe closing preset panel`);
        
        // Reseta estado de seleção de preset
        this.smartPanelManager.setCurrentPreset('standard'); // Reset para padrão
        
        // Força seletor de preset a atualizar
        this.cdr.markForCheck();
        
        // Pequeno delay para garantir que o estado seja atualizado
        setTimeout(() => {
          resolve();
        }, 10);
        
      } catch (error) {
        console.error(`❌ Error closing preset panel:`, error);
        resolve(); // Não bloqueia o processo
      }
    });
  }
  
  /**
   * Fecha com segurança o painel de edição rápida com proteção de timeout
   */
  private async safeCloseQuickEditPanel(): Promise<void> {
    return new Promise((resolve) => {
      try {
        console.log(`⚡ Safe closing quick edit panel`);
        
        // Verifica se o editor inline está ativo e o fecha
        const inlineEditorElement = document.querySelector('.inline-editor-overlay');
        if (inlineEditorElement) {
          // Dispara evento de fechamento ou remove o elemento
          const closeButton = inlineEditorElement.querySelector('.close-button, .cancel-button');
          if (closeButton) {
            (closeButton as HTMLElement).click();
          }
          
          // Aguarda um pouco pela animação de fechamento
          setTimeout(() => {
            resolve();
          }, 100);
        } else {
          resolve();
        }
        
              } catch (error) {
          console.error(`❌ Error closing quick edit panel:`, error);
          resolve(); // Não bloqueia o processo
        }
    });
  }
  
  /**
   * Fecha com segurança diálogos modais com proteção de timeout
   */
  private async safeCloseModalDialogs(): Promise<void> {
    return new Promise((resolve) => {
      try {
        console.log(`🔲 Safe closing modal dialogs`);
        
        // Fecha qualquer instância NZ-Modal que possa estar aberta
        const modalElements = document.querySelectorAll('.ant-modal-wrap');
        if (modalElements.length > 0) {
          modalElements.forEach(modal => {
            const closeButton = modal.querySelector('.ant-modal-close, .ant-modal-close-x');
            if (closeButton) {
              (closeButton as HTMLElement).click();
            }
          });
          
          // Aguarda animações de fechamento do modal
          setTimeout(() => {
            resolve();
          }, 200);
        } else {
          resolve();
        }
        
              } catch (error) {
          console.error(`❌ Error closing modal dialogs:`, error);
          resolve(); // Não bloqueia o processo
        }
    });
  }
  
  /**
   * Reseta com segurança estados temporários da UI com proteção de timeout
   */
  private async safeResetTemporaryStates(): Promise<void> {
    return new Promise((resolve) => {
      try {
        console.log(`🔄 Safe resetting temporary UI states`);
        
        // Reseta qualquer flag ou estado temporário que não deve persistir
        // entre trocas de componentes
        
        // Força uma atualização completa da UI
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        
        // Pequeno delay para garantir que as mudanças sejam aplicadas
        setTimeout(() => {
          resolve();
        }, 50);
        
      } catch (error) {
        console.error(`❌ Error resetting temporary states:`, error);
        resolve(); // Não bloqueia o processo
      }
    });
  }

  /**
   * Legacy methods for backward compatibility
   */
  private closePresetPanel() {
    this.safeClosePresetPanel().catch(error => {
      console.error(`❌ Error in legacy closePresetPanel:`, error);
    });
  }
  
  // TEMPORARIAMENTE DESABILITADO: Edição Rápida
  // private closeQuickEditPanel() {
  //   this.safeCloseQuickEditPanel().catch(error => {
  //     console.error(`❌ Error in legacy closeQuickEditPanel:`, error);
  //   });
  // }
  
  private closeModalDialogs() {
    this.safeCloseModalDialogs().catch(error => {
      console.error(`❌ Error in legacy closeModalDialogs:`, error);
    });
  }
  
  private resetTemporaryStates() {
    this.safeResetTemporaryStates().catch(error => {
      console.error(`❌ Error in legacy resetTemporaryStates:`, error);
    });
  }

  /**
   * CORREÇÃO CRÍTICA: Gerencia mudanças de propriedades de cor com bloqueios de operação aprimorados
   */
  onPropertyChange(property: string, value: any): void {
    // Tratamento especial para propriedades de cor
    if (this.isColorProperty(property)) {
      this.handleColorPropertyChange(property, value);
      return;
    }

    // Lógica existente de mudança de propriedades para propriedades não-cor
    this.handleRegularPropertyChange(property, value);
  }

  private handleColorPropertyChange(property: string, value: string): void {
    // Previne atualizações concorrentes de cor
    if (this.isUpdatingColorProperty) {
      console.log(`🎨 Atualização de cor em andamento, enfileirando: ${property} = ${value}`);
      this.colorUpdateQueue.push({ property, value });
      return;
    }

    this.isUpdatingColorProperty = true;

    // Processa atualização de cor
    this.processColorUpdate(property, value).then(() => {
      this.isUpdatingColorProperty = false;

      // Processa atualizações enfileiradas
      if (this.colorUpdateQueue.length > 0) {
        const next = this.colorUpdateQueue.shift()!;
        console.log(`🎨 Processando atualização de cor enfileirada: ${next.property} = ${next.value}`);
        this.handleColorPropertyChange(next.property, next.value);
      }
    });
  }

  private async processColorUpdate(property: string, value: string): Promise<void> {
    return new Promise((resolve) => {
      // Usa requestAnimationFrame para garantir atualizações suaves
      requestAnimationFrame(() => {
        try {
          const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
          if (selectedComponent) {
            console.log(`🎨 Processando atualização de cor: ${property} = ${value} para componente ${selectedComponent}`);
            this.builderService.updateNodeProps(selectedComponent, { [property]: value });
            this.cdr.markForCheck();
          }
          resolve();
        } catch (error) {
          console.error(`❌ Erro na atualização de cor para ${property}:`, error);
          resolve();
        }
      });
    });
  }

  private handleRegularPropertyChange(property: string, value: any): void {
    // Gerencia mudanças de propriedades não-cor imediatamente
    const selectedComponent = this.uiState.getCurrentState().inspector.selectedComponent;
    if (selectedComponent) {
      this.builderService.updateNodeProps(selectedComponent, { [property]: value });
      this.cdr.markForCheck();
    }
  }

  private isColorProperty(property: string): boolean {
    return property.toLowerCase().includes('color') || 
           property.toLowerCase().includes('background') ||
           property === 'backgroundColor' ||
           property === 'textColor' ||
           property === 'borderColor' ||
           property === 'hoverColor' ||
           property === 'activeTabColor' ||
           property === 'headerBackgroundColor' ||
           property === 'rowBackgroundColor' ||
           property === 'alternateRowColor';
  }

  // MÉTODO DE DEBUG: Testa funcionalidade de preset
  testPresetFunctionality() {
    console.log(`🧪 TESTANDO FUNCIONALIDADE DE PRESET`);
    
    const currentState = this.uiState.getCurrentState();
    const currentComponent = currentState.inspector.selectedComponent;
    
    console.log(`📋 Componente atual: ${currentComponent}`);
    console.log(`📋 Preset atual: ${this.smartPanelManager.getCurrentPreset()}`);
    console.log(`📋 selectedComponent$:`, this.selectedComponent$);
    console.log(`📋 currentComponent$:`, this.currentComponent$);
    
    // Verifica observables
    this.selectedComponent$.pipe(take(1)).subscribe(comp => {
      console.log(`📋 valor de selectedComponent$:`, comp);
    });
    
    this.currentComponent$.pipe(take(1)).subscribe(comp => {
      console.log(`📋 valor de currentComponent$:`, comp);
    });
    
    // Testa cada preset
    const presets = ['minimal', 'standard', 'advanced'];
    presets.forEach((preset, index) => {
      setTimeout(() => {
        console.log(`🔄 Testando preset: ${preset}`);
        this.onPresetSelected(preset);
      }, index * 2000); // Intervalos de 2 segundos
    });
  }
}
